// src/pages/AdminFiles.js
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { apiFetch, formatDateTime, formatDate, formatBytes, timeUntil, isManuallyDeleted, deletedLabel } from '../utils/api';
import TransferProgress from '../components/TransferProgress';
import PasswordResetModal from '../components/PasswordResetModal';

const ACTION_LABELS = {
  transfer_created: 'Created', email_resent: 'Email Resent', email_failed: 'Email Failed', password_reset: 'Password Reset', password_failed: 'Password Failed', otp_requested: 'OTP Sent', otp_verified: 'OTP Verified',
  otp_failed: 'OTP Failed', otp_blocked: 'OTP Blocked', file_downloaded: 'Downloaded',
  file_viewed: 'Viewed', link_expired: 'Expired', link_unexpired: 'Unexpired',
  link_regenerated: 'Regen Link', transfer_deleted: 'Deleted',
};

function StatusBadge({ status }) {
  const map = {
    active: ['status-active', '● Active'],
    'expired-downloaded': ['status-expired-downloaded', '✓ Downloaded'],
    'expired-timeout': ['status-expired-timeout', '⏱ Timed out'],
    deleted: ['status-deleted', '🗑 Deleted'],
  };
  const [cls, label] = map[status] || ['status-deleted', status];
  return <span className={`status-badge ${cls}`}>{label}</span>;
}

function AuditModal({ transferId, title, onClose }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    apiFetch(`mgmt/audit/${transferId}`).then(d => setEntries(d.entries || [])).catch(console.error).finally(() => setLoading(false));
  }, [transferId]);
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ width: '680px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3>Audit Log — {title}</h3><button className="modal-close" onClick={onClose}>×</button></div>
        <div style={{ overflow: 'auto', flex: 1, padding: '16px 24px' }}>
          {loading ? <div style={{ textAlign: 'center', padding: '32px' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          : entries.length === 0 ? <p style={{ color: 'var(--g400)', textAlign: 'center', padding: '32px' }}>No audit entries</p>
          : entries.map((e, i) => {
            const isRecipientAction = ['file_downloaded','file_viewed','otp_requested','otp_verified','otp_failed','otp_blocked'].includes(e.action);
            const showEmail = isRecipientAction ? e.recipientEmail : e.uploaderEmail;
            return (
              <div key={i} className="audit-entry">
                <span className="audit-time">{formatDateTime(e.timestamp)}</span>
                <span className={`audit-action audit-${e.action}`}>{ACTION_LABELS[e.action] || e.action}</span>
                <span className="audit-email" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{showEmail || '—'}</span>
                <span className="audit-detail">{e.detail || '—'}</span>
              </div>
            );
          })}
        </div>
        <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
}

function AddFilesModal({ transfer, onDone, onClose }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({});
  const [error, setError] = useState('');
  const fileInputRef = useRef();

  const handleUpload = async () => {
    if (!files.length) return;
    setUploading(true); setError('');
    try {
      const data = await apiFetch(`mgmt/transfers/${transfer.id}/files`, {
        method: 'POST',
        body: JSON.stringify({ fileNames: files.map(f => f.name), fileSizes: files.map(f => f.size) }),
      });
      for (const file of files) {
        const sasUrl = data.uploadUrls[file.name];
        if (!sasUrl) continue;
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('PUT', sasUrl);
          xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
          xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
          xhr.upload.addEventListener('progress', e => {
            if (e.lengthComputable) setProgress(p => ({ ...p, [file.name]: Math.round(e.loaded / e.total * 100) }));
          });
          xhr.onload = () => xhr.status < 300 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`));
          xhr.onerror = () => reject(new Error('Network error'));
          xhr.send(file);
        });
      }
      onDone();
    } catch (e) { setError(e.message); setUploading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3>➕ Add Files</h3><button className="modal-close" onClick={onClose}>×</button></div>
        <div className="modal-body">
          <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={e => setFiles(f => [...f, ...Array.from(e.target.files || [])])} />
          <button className="btn btn-secondary" style={{ width: '100%', justifyContent: 'center', marginBottom: '10px' }} onClick={() => fileInputRef.current.click()}>📂 Choose files</button>
          {files.map(f => (
            <div key={f.name} className="file-item" style={{ marginBottom: '5px' }}>
              <span>📄</span><span className="file-item-name">{f.name}</span><span className="file-item-size">{formatBytes(f.size)}</span>
              {uploading && progress[f.name] !== undefined && <div className="progress-bar-wrap" style={{ width: '60px' }}><div className="progress-bar" style={{ width: `${progress[f.name]}%` }} /></div>}
              {!uploading && <button className="file-remove" onClick={() => setFiles(fs => fs.filter(x => x.name !== f.name))}>×</button>}
            </div>
          ))}
          {error && <div className="alert alert-error" style={{ marginTop: '10px' }}>{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={uploading}>Cancel</button>
          <button className="btn btn-primary" onClick={handleUpload} disabled={uploading || !files.length}>{uploading ? 'Uploading…' : `Upload ${files.length} file${files.length !== 1 ? 's' : ''}`}</button>
        </div>
      </div>
    </div>
  );
}


function AdminDownloadBtn({ transfer, showToast }) {
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState('');

  const download = async (fileName) => {
    setLoading(fileName);
    try {
      const data = await apiFetch(`mgmt/admin-download/${transfer.id}/${encodeURIComponent(fileName)}`);
      const a = document.createElement('a');
      a.href = data.downloadUrl;
      a.download = fileName;
      a.click();
      showToast(`✅ Downloading ${fileName}`);
    } catch (e) { showToast(`❌ ${e.message}`); }
    finally { setLoading(''); setOpen(false); }
  };

  if (!open) return (
    <button className="btn btn-secondary btn-sm" onClick={() => setOpen(true)}>⬇️ Download</button>
  );

  return (
    <div className="modal-overlay" onClick={() => setOpen(false)}>
      <div className="modal" style={{ width: '400px' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3>⬇️ Admin Download</h3><button className="modal-close" onClick={() => setOpen(false)}>×</button></div>
        <div className="modal-body">
          <div className="alert alert-warn" style={{ marginBottom: '12px' }}>This download will be recorded in the audit log.</div>
          {(transfer.fileNames || []).map((name, i) => (
            <div key={i} className="file-item" style={{ marginBottom: '6px' }}>
              <span>📄</span>
              <span className="file-item-name" style={{ flex: 1 }}>{name}</span>
              <button className="btn btn-primary btn-sm" onClick={() => download(name)} disabled={!!loading}>
                {loading === name ? '…' : '⬇️'}
              </button>
            </div>
          ))}
        </div>
        <div className="modal-footer"><button className="btn btn-secondary" onClick={() => setOpen(false)}>Cancel</button></div>
      </div>
    </div>
  );
}

function TransferCard({ transfer, onRefresh, showToast, onAudit }) {
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState('');
  const [newPassword, setNewPassword] = useState(null);
  const isActive = transfer.status === 'active';
  const isExpired = !isActive && transfer.status !== 'deleted';

  const action = async (key, fn, msg) => {
    setBusy(key);
    try { await fn(); await onRefresh(); showToast(msg); }
    catch (e) { showToast(`❌ ${e.message}`); }
    finally { setBusy(''); }
  };

  const adminResetPassword = async () => {
    if (!window.confirm(`Generate a new password for ${transfer.recipientEmail}? The current password will stop working immediately.`)) return;
    setBusy('reset');
    try {
      const r = await apiFetch(`mgmt/transfers/${transfer.id}/reset-password`, { method: 'POST', body: '{}' });
      setNewPassword(r.password);   // no list refresh here — it could unmount this card and lose the password
    } catch (e) { showToast(`❌ ${e.message}`); }
    finally { setBusy(''); }
  };

  const removeFile = name => {
    if (!window.confirm(`Remove "${name}"?`)) return;
    action('file', () => apiFetch(`mgmt/transfers/${transfer.id}/files`, { method: 'DELETE', body: JSON.stringify({ fileName: name }) }), '✅ File removed');
  };

  return (
    <div className={`transfer-card${!isActive ? ' expired' : ''}`} style={{ display: 'block', padding: 0 }}>
      <div style={{ padding: '14px 18px 10px', borderBottom: '1px solid var(--g100)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px', flexWrap: 'wrap' }}>
          <div className="transfer-title">{transfer.title}</div>
          <StatusBadge status={transfer.status} />
          {transfer.viewOnly && <span className="status-badge" style={{ background: '#ede9fe', color: '#6d28d9' }}>👁 View only</span>}
          {transfer.hasPassword && <span className="status-badge" style={{ background: '#fff8e1', color: '#b45309' }}>🔑 Password</span>}
        </div>
        <div className="transfer-meta">
          <span style={{ fontWeight: 600 }}>{transfer.uploaderName}</span>
          <span style={{ color: 'var(--g500)' }}>{transfer.uploaderEmail}</span>
          <span className="tp-recipient">→ {transfer.recipientEmail}</span>
          <span>📅 {formatDate(transfer.createdAt)}</span>
          {isActive && <span style={{ color: 'var(--green)', fontWeight: 600 }}>⏱ {timeUntil(transfer.expiresAt)}</span>}
          {!isActive && transfer.status !== 'deleted' && transfer.expiredAt && <span>Expired {formatDate(transfer.expiredAt)}</span>}
          {transfer.status === 'deleted' && <span style={{ color: 'var(--red)' }}>🗑 {deletedLabel(transfer, { admin: true })}</span>}
        </div>
      </div>

      <div style={{ padding: '10px 18px', borderBottom: '1px solid var(--g100)' }}>
        <TransferProgress transfer={transfer} kind="file" horizontal />
      </div>

      <div style={{ padding: '10px 18px', borderBottom: '1px solid var(--g100)' }}>
        <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--g400)', marginBottom: '6px' }}>Files ({transfer.fileNames?.length || 0})</div>
        {(transfer.fileNames || []).map((name, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '12px' }}>📄</span>
            <span style={{ fontSize: '12px', flex: 1, color: 'var(--g700)', wordBreak: 'break-all' }}>{name}</span>
            <span style={{ fontSize: '11px', color: 'var(--g400)', whiteSpace: 'nowrap' }}>{formatBytes(transfer.fileSizes?.[i])}</span>
            {isActive && (transfer.fileNames?.length || 0) > 1 && (
              <button disabled={!!busy} onClick={() => removeFile(name)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)', fontSize: '14px', fontWeight: 700, padding: '0 2px', lineHeight: 1 }}>×</button>
            )}
          </div>
        ))}
        {isActive && (
          <button className="btn btn-secondary btn-sm" style={{ marginTop: '8px' }} onClick={() => setModal('addFile')} disabled={!!busy}>+ Add files</button>
        )}
      </div>

      <div style={{ padding: '10px 18px', background: 'var(--g50)', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {isActive && (
          <>
            {transfer.hasPassword && <button className="btn btn-secondary btn-sm" disabled={!!busy} onClick={adminResetPassword}>{busy === 'reset' ? '…' : '🔑 Reset password'}</button>}
            <button className="btn btn-secondary btn-sm" disabled={!!busy} onClick={() => action('extend', () => apiFetch(`mgmt/transfers/${transfer.id}/extend`, { method: 'PUT', body: '{}' }), '✅ Extended 3 days')}>⏱ Extend 3 days</button>
            <button className="btn btn-sm" style={{ background: 'var(--red-light)', color: 'var(--red)', border: '1px solid var(--red)' }} disabled={!!busy}
              onClick={() => action('expire', () => apiFetch(`mgmt/transfers/${transfer.id}/expire`, { method: 'PUT', body: '{}' }), '✅ Expired')}>✕ Expire now</button>
          </>
        )}
        {isExpired && (
          <>
            {transfer.status !== 'expired-downloaded' && (
              <button className="btn btn-secondary btn-sm" disabled={!!busy} onClick={() => action('reactivate', () => apiFetch(`mgmt/transfers/${transfer.id}/extend`, { method: 'PUT', body: '{}' }), '✅ Re-activated')}>🔄 Re-activate (3 days)</button>
            )}
            <button className="btn btn-sm" style={{ background: 'var(--red-light)', color: 'var(--red)', border: '1px solid var(--red)' }} disabled={!!busy}
              onClick={() => { if (!window.confirm('Delete this transfer?')) return; action('delete', () => apiFetch(`mgmt/transfers/${transfer.id}`, { method: 'DELETE', body: '{}' }), '✅ Deleted'); }}>🗑 Delete</button>
          </>
        )}
        <button className="btn btn-secondary btn-sm" onClick={() => onAudit(transfer)}>🔍 Audit log</button>
        {(isActive || isExpired) && (transfer.fileNames || []).length > 0 && (
          <AdminDownloadBtn transfer={transfer} showToast={showToast} />
        )}
      </div>

      {newPassword && <PasswordResetModal password={newPassword} recipientEmail={transfer.recipientEmail} onClose={() => setNewPassword(null)} />}
      {modal === 'addFile' && <AddFilesModal transfer={transfer} onClose={() => setModal(null)} onDone={() => { setModal(null); onRefresh(); showToast('✅ Files added'); }} />}
    </div>
  );
}

export default function AdminFiles() {
  const [data, setData] = useState({ active: [], expired: [], total: 0 });
  const [globalAudit, setGlobalAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [auditLoading, setAuditLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [tab, setTab] = useState('active');
  const [search, setSearch] = useState('');
  const [auditModal, setAuditModal] = useState(null);
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setLoadError('');
    try { setData(await apiFetch('mgmt/transfers')); }
    catch (e) { setLoadError(e.message); }
    finally { setLoading(false); }
  }, []);

  const loadAudit = useCallback(async () => {
    setAuditLoading(true);
    try { const d = await apiFetch('mgmt/audit'); setGlobalAudit(d.entries || []); }
    catch (e) { console.error(e); }
    finally { setAuditLoading(false); }
  }, []);

  useEffect(() => { load(); loadAudit(); }, [load, loadAudit]);

  const showToast = msg => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  const filter = list => !search ? list : list.filter(t =>
    t.title?.toLowerCase().includes(search.toLowerCase()) ||
    t.uploaderEmail?.toLowerCase().includes(search.toLowerCase()) ||
    t.recipientEmail?.toLowerCase().includes(search.toLowerCase())
  );

  const completedList = (data.expired || []).filter(t => t.status === 'expired-downloaded');   // downloaded / viewed
  const deletedList   = (data.expired || []).filter(isManuallyDeleted);                                       // deleted on purpose
  const expiredList   = (data.expired || []).filter(t => t.status !== 'expired-downloaded' && !isManuallyDeleted(t));   // timed out / auto-purged
  const shown = tab === 'active' ? filter(data.active) : tab === 'completed' ? filter(completedList) : tab === 'expired' ? filter(expiredList) : tab === 'deleted' ? filter(deletedList) : [];

  return (
    <div>
      <div className="page-eyebrow">Administration</div>
      <h1 className="page-title">Manage File Transfers</h1>
      <p className="page-subtitle">View, audit and manage all file transfers across the organisation</p>

      <div className="stat-grid">
        <div className="stat-card stat-card-red"><div className="stat-icon stat-icon-red">📁</div><div className="stat-info"><div className="stat-value">{data.total}</div><div className="stat-label">Total</div></div></div>
        <div className="stat-card stat-card-green"><div className="stat-icon stat-icon-green">✅</div><div className="stat-info"><div className="stat-value">{data.active?.length || 0}</div><div className="stat-label">Active</div></div></div>
        <div className="stat-card stat-card-blue"><div className="stat-icon stat-icon-blue">⬇️</div><div className="stat-info"><div className="stat-value">{data.expired?.filter(t => t.status === 'expired-downloaded').length || 0}</div><div className="stat-label">Downloaded</div></div></div>
        <div className="stat-card stat-card-amber"><div className="stat-icon stat-icon-amber">⏱</div><div className="stat-info"><div className="stat-value">{data.expired?.filter(t => t.status === 'expired-timeout').length || 0}</div><div className="stat-label">Expired (no DL)</div></div></div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="tabs" style={{ marginBottom: 0, borderBottom: 'none' }}>
            <button className={`tab${tab === 'active' ? ' active' : ''}`} onClick={() => setTab('active')}>Active <span className="tab-badge">{data.active?.length || 0}</span></button>
            <button className={`tab${tab === 'completed' ? ' active' : ''}`} onClick={() => setTab('completed')}>Completed <span className="tab-badge">{completedList.length}</span></button>
            <button className={`tab${tab === 'expired' ? ' active' : ''}`} onClick={() => setTab('expired')}>Expired <span className="tab-badge">{expiredList.length}</span></button>
            <button className={`tab${tab === 'deleted' ? ' active' : ''}`} onClick={() => setTab('deleted')}>Deleted <span className="tab-badge">{deletedList.length}</span></button>
            <button className={`tab${tab === 'audit' ? ' active' : ''}`} onClick={() => setTab('audit')}>Audit Log</button>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {tab !== 'audit' && <input className="form-input" style={{ width: '220px' }} placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} />}
            <button className="btn btn-secondary btn-sm" onClick={() => { load(); loadAudit(); }}>↻</button>
          </div>
        </div>
        <div className="card-body">
          {loadError ? <div className="alert alert-error">❌ {loadError}</div>
          : tab === 'audit' ? (
            auditLoading ? <div style={{ textAlign: 'center', padding: '40px' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
            : globalAudit.length === 0 ? <div className="empty-state"><div className="empty-state-icon">📋</div><div className="empty-state-title">No audit entries</div></div>
            : globalAudit.map((e, i) => {
                const showEmail = e.action === 'file_downloaded' ? e.recipientEmail : e.uploaderEmail;
                return (
              <div key={i} className="audit-entry">
                <span className="audit-time">{formatDateTime(e.timestamp)}</span>
                <span className={`audit-action audit-${e.action}`}>{ACTION_LABELS[e.action] || e.action}</span>
                <span className="audit-email" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{showEmail || '—'}</span>
                <span className="audit-detail">{e.detail || '—'}</span>
              </div>
            );
              })
          ) : loading ? <div style={{ textAlign: 'center', padding: '40px' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          : shown.length === 0 ? <div className="empty-state"><div className="empty-state-icon">🗂</div><div className="empty-state-title">No transfers found</div></div>
          : (
            <div className="transfer-list">
              {shown.map(t => <TransferCard key={t.id} transfer={t} onRefresh={load} showToast={showToast} onAudit={setAuditModal} />)}
            </div>
          )}
        </div>
      </div>

      {auditModal && <AuditModal transferId={auditModal.id} title={auditModal.title} onClose={() => setAuditModal(null)} />}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
